import { useState, useMemo, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DesktopLayout from '../layout/DesktopLayout';
import { useOffers } from './store/useOffers';
import { useLocations } from './store/useLocations';
import { useAuth } from '../../context/AuthContext';
import { useBranch } from '../../context/BranchContext';
import { useDesktopTopBar } from '../../context/DesktopLayoutContext';
import { useOfferManagement, useOfferToastStore } from '../../redux/offers/offerHooks';
import { useAppDispatch } from '../../redux/hooks';
import { offersClearCache } from '../../redux/offers/offerActions';
import { markOffersListVisited } from './store/offersSessionFlags';
import { Button, Tabs, ConfirmDialog, Badge } from './components/ui';
import { Modal } from './components/ui/Modal';
import DataTable from './components/data/DataTable';
import StatusBadge from './components/data/StatusBadge';
import { SearchInput, Select, CheckboxGroup } from './components/forms';
import { formatDate } from './utils/format';
import PageHeader from './components/layout/PageHeader';
import Pagination from './components/data/Pagination';
import { IconTag, IconActivity, IconDollar } from './components/ui/Icons';
import KPICard from './components/data/KPICard';
import KPIRow from './components/data/KPIRow';
import { formatNumber, formatCurrency } from './utils/format';
import {
  OFFER_LIST_ACTION_ICON_PX,
  offerListActionButtonStyle,
  offerListActionEnterDefault,
  offerListActionLeaveDefault,
  offerListActionEnterDanger,
  offerListActionLeaveDanger,
} from './utils/listActions';
import type { Offer, OfferType, OfferStatus, OfferOrderType } from './data/types';
import { loadOfferMenuItems } from './data/mockMenuItems';
import './OffersListPage.css';


const offerTypeLabels: Record<OfferType, { label: string; variant: 'accent' | 'success' | 'warning' }> = {
  'percentage-off': { label: '% Off', variant: 'accent' },
  'flat-dollar-off': { label: '$ Off', variant: 'success' },
  'bogo': { label: 'BOGO', variant: 'warning' },
  'free-item': { label: 'Free Item', variant: 'accent' },
  'free-delivery': { label: 'Free Delivery', variant: 'success' },
  'combo-bundle': { label: 'Combo', variant: 'success' },   // legacy
  'happy-hour': { label: 'Happy Hour', variant: 'warning' }, // legacy
  'first-time': { label: '1st Visit', variant: 'accent' },
  'loyalty-linked': { label: 'Loyalty', variant: 'success' }, // legacy
};

const TYPE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All Types' },
  { value: 'percentage-off', label: 'Percentage Off' },
  { value: 'flat-dollar-off', label: 'Flat $ Off' },
  { value: 'free-item', label: 'Free Item' },
  { value: 'free-delivery', label: 'Free Delivery' },
];

// Fallback labels for offers whose `orderTypes` are stored as slugs (local/mock data).
// Offers from the API store outlet order-type UUIDs, resolved via the branch order types.
const ORDER_TYPE_SLUG_LABELS: Record<OfferOrderType, string> = {
  'dine-in': 'Dine-In',
  'walk-in': 'Walk-In',
  'phone': 'Phone Order',
  'table-kiosk': 'Table Kiosk',
  'online-pickup': 'Online Pickup',
  'online-delivery': 'Online Delivery',
  'station-kiosk': 'Station Kiosk',
};

function formatDiscount(offer: Offer): string {
  if (offer.type === 'percentage-off' || offer.type === 'first-time') {
    return `${offer.discountValue}%`;
  }
  if (offer.type === 'flat-dollar-off') return `$${offer.discountValue.toFixed(2)}`;
  if (offer.type === 'bogo') return 'BOGO';
  if (offer.type === 'combo-bundle' && offer.typeConfig?.kind === 'combo-bundle') {
    return `$${offer.typeConfig.bundlePrice.toFixed(2)}`;
  }
  if (offer.type === 'free-item') return 'Free Item';
  return '-';
}

// Offer types whose Discount column shows no numeric value (BOGO, Free Item,
// Free Delivery). When sorting by Discount these always sink to the bottom,
// regardless of sort direction.
const NON_NUMERIC_DISCOUNT_TYPES = new Set<string>(['bogo', 'free-item', 'free-delivery']);

function hasNumericDiscount(offer: Offer): boolean {
  return !NON_NUMERIC_DISCOUNT_TYPES.has(offer.type);
}

function getOfferPromoCodeStrings(offer: Offer): string[] {
  const mappedCodes = Array.isArray(offer.promoCodes)
    ? offer.promoCodes.map((c) => String(c.code ?? '').trim()).filter(Boolean)
    : [];

  const rawOffer = offer as unknown as Record<string, unknown>;
  const rawList = Array.isArray(rawOffer.promoCodes)
    ? rawOffer.promoCodes
    : Array.isArray(rawOffer.promo_codes)
      ? rawOffer.promo_codes
      : rawOffer.promoCode
        ? [rawOffer.promoCode]
        : rawOffer.promo_code
          ? [rawOffer.promo_code]
          : [];

  const rawCodes = rawList
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return '';
      const row = entry as Record<string, unknown>;
      return String(row.code ?? row.name ?? '').trim();
    })
    .filter(Boolean);

  return Array.from(new Set([...mappedCodes, ...rawCodes]));
}

type SortKey = 'name' | 'type' | 'discount' | 'status' | 'createdAt' | 'redemptions';
type SortDir = 'asc' | 'desc';

const PAGE_SIZES = [25, 50, 100];

export default function OffersListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setTopBarProps = useDesktopTopBar();
  const dispatch = useAppDispatch();
  const { auth } = useAuth();
  const { selectedBranch } = useBranch();
  const {
    fetchOffers,
    deleteOfferOnServer,
    bulkDeleteOffersOnServer,
    toggleOfferStatusOnServer,
    bulkArchiveOffersOnServer,
    setEditingOfferId,
    offers: { offers: apiOffers, loading: offersLoading, error: offersError, summary },
  } = useOfferManagement();
  const { offers: localOffers, deleteOffer, archiveOffer, transitionStatus } = useOffers();
  const { locations } = useLocations();
  const { addToast } = useOfferToastStore();
  const offers = apiOffers.length > 0 ? apiOffers : localOffers;
  const showInitialLoading = offersLoading && offers.length === 0;

  useEffect(() => {
    markOffersListVisited();
  }, []);

  // Map outlet order-type UUID → display name for the Order Types column.
  const orderTypeNameById = useMemo(() => {
    const map: Record<string, string> = {};
    const raw = (selectedBranch as Record<string, any> | null | undefined)?.orderTypes;
    if (Array.isArray(raw)) {
      raw.forEach((entry) => {
        const id = String(entry?.id ?? '').trim();
        const name = String(entry?.typeName ?? '').trim();
        if (id && name) map[id] = name;
      });
    }
    return map;
  }, [selectedBranch]);

  // Resolve each stored order type (UUID or legacy slug) to its display name.
  const resolveOrderTypeNames = useCallback(
    (orderTypes: Offer['orderTypes']): string[] => {
      const seen = new Set<string>();
      const names: string[] = [];
      (orderTypes ?? []).forEach((ot) => {
        const key = String(ot);
        const name = orderTypeNameById[key] ?? ORDER_TYPE_SLUG_LABELS[ot as OfferOrderType];
        if (name && !seen.has(name)) {
          seen.add(name);
          names.push(name);
        }
      });
      return names;
    },
    [orderTypeNameById],
  );
  const useServerOffers = apiOffers.length > 0;

  const persistOfferStatus = useCallback(
    async (
      offerId: string,
      next: OfferStatus,
      toastCfg: { msg: string; variant: 'success' | 'warning' },
    ) => {
      const locationId = selectedBranch?.id ?? selectedBranch?.locationId ?? '';
      const token = auth?.accessToken ?? auth?.access_token ?? null;
      const canPatch =
        useServerOffers &&
        Boolean(locationId) &&
        Boolean(token) &&
        (next === 'active' || next === 'paused' || next === 'draft' || next === 'archived');
      if (canPatch) {
        try {
          await toggleOfferStatusOnServer(offerId, locationId, next, token);
          transitionStatus(offerId, next);
          await fetchOffers(locationId, token, { force: true });
          addToast(toastCfg.msg, toastCfg.variant === 'warning' ? 'warning' : 'success');
        } catch (e) {
          addToast(e instanceof Error ? e.message : 'Failed to update offer status', 'error');
        }
        return;
      }
      if (transitionStatus(offerId, next)) {
        addToast(toastCfg.msg, toastCfg.variant === 'warning' ? 'warning' : 'success');
      }
    },
    [
      useServerOffers,
      selectedBranch?.id,
      selectedBranch?.locationId,
      auth?.accessToken,
      auth?.access_token,
      toggleOfferStatusOnServer,
      transitionStatus,
      fetchOffers,
      addToast,
    ],
  );

  const [activeTab, setActiveTab] = useState('active');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [confirmAction, setConfirmAction] = useState<{ type: 'delete' | 'archive' | 'pause'; id: string } | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Bulk assign modal state

  // Fetch offers from server on mount / when location or auth changes.
  // Clear stale data immediately so the previous branch's offers never flash while loading.
  useEffect(() => {
    const locationId = selectedBranch?.id ?? selectedBranch?.locationId ?? '';
    const token = auth?.accessToken ?? auth?.access_token ?? null;
    if (!locationId) return;
    dispatch(offersClearCache());
    void fetchOffers(locationId, token, { force: true });
  }, [selectedBranch?.id, selectedBranch?.locationId, auth?.accessToken, auth?.access_token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Preload menu items into the module cache so the edit form can show
  // selected item names without firing its own API call.
  useEffect(() => {
    const locationId = selectedBranch?.id ?? selectedBranch?.locationId ?? '';
    const token = auth?.accessToken ?? auth?.access_token ?? null;
    if (!locationId) return;
    void loadOfferMenuItems(locationId, token).catch(() => { /* ignore — form falls back to empty */ });
  }, [selectedBranch?.id, selectedBranch?.locationId, auth?.accessToken, auth?.access_token]);

  // Sidebar re-click passes resetOffersTab to force default tab on /offers.
  useEffect(() => {
    if (location.pathname !== '/offers') return;
    const state = location.state as { resetOffersTab?: boolean } | null | undefined;
    if (!state?.resetOffersTab) return;
    setActiveTab('active');
    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: {},
    });
  }, [location.pathname, location.search, location.hash, location.state, navigate]);

  // Ensure DesktopLayout top bar always reflects Offers screen on this route.
  useEffect(() => {
    if (location.pathname !== '/offers') return;
    setTopBarProps({ title: 'Offers & Discounts', breadcrumbs: '' });
  }, [setTopBarProps, location.pathname, location.search, location.hash, location.state]);

  const filtered = useMemo(() => {
    const inactiveStatuses = ['paused', 'expired', 'scheduled', 'archived'];
    let result = activeTab === 'inactive'
      ? offers.filter((o) => inactiveStatuses.includes(o.status))
      : offers.filter((o) => o.status === activeTab);

    // Type filter
    if (typeFilter) {
      result = result.filter((o) => o.type === typeFilter);
    }

    // Search
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter((o) =>
        o.name.toLowerCase().includes(q) ||
        o.description.toLowerCase().includes(q) ||
        getOfferPromoCodeStrings(o).some((code) => code.toLowerCase().includes(q))
      );
    }

    // Sort
    result = [...result].sort((a, b) => {
      // Discount: offers with no numeric discount (BOGO, Free Item, Free Delivery)
      // always sink to the bottom, independent of sort direction.
      if (sortKey === 'discount') {
        const aNum = hasNumericDiscount(a);
        const bNum = hasNumericDiscount(b);
        if (aNum !== bNum) return aNum ? -1 : 1;
        if (!aNum) return 0;
        const cmp = a.discountValue - b.discountValue;
        return sortDir === 'asc' ? cmp : -cmp;
      }

      let cmp = 0;
      switch (sortKey) {
        case 'name': cmp = a.name.localeCompare(b.name); break;
        case 'type': cmp = a.type.localeCompare(b.type); break;
        case 'status': cmp = a.status.localeCompare(b.status); break;
        case 'createdAt': cmp = a.createdAt.localeCompare(b.createdAt); break;
        case 'redemptions': cmp = a.redemptionCount - b.redemptionCount; break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [offers, activeTab, search, typeFilter, sortKey, sortDir]);

  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const tabsWithCounts = useMemo(() => {
    const inactiveStatuses = ['paused', 'expired', 'scheduled', 'archived'];
    const counts = {
      active: offers.filter((o) => o.status === 'active').length,
      draft: offers.filter((o) => o.status === 'draft').length,
      inactive: offers.filter((o) => inactiveStatuses.includes(o.status)).length,
    };
    return [
      { key: 'active', label: `Active (${counts.active})` },
      { key: 'draft', label: `Draft (${counts.draft})` },
      { key: 'inactive', label: `Inactive (${counts.inactive})` },
    ];
  }, [offers]);

  // Reset page when filters change
  useEffect(() => setPage(1), [activeTab, search, typeFilter]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const handleConfirm = async () => {
    if (!confirmAction) return;
    const { type, id } = confirmAction;
    const locationId = selectedBranch?.id ?? selectedBranch?.locationId ?? '';
    const token = auth?.accessToken ?? auth?.access_token ?? null;
    if (type === 'delete') {
      if (!locationId) {
        addToast('Location is required to delete offer', 'error');
      } else {
        try {
          await deleteOfferOnServer(id, locationId, token);
          deleteOffer(id);
          await fetchOffers(locationId, token, { force: true });
          addToast('Offer deleted', 'warning');
        } catch (e) {
          addToast(e instanceof Error ? e.message : 'Failed to delete offer', 'error');
        }
      }
    } else if (type === 'archive') {
      // Optimistically update local state then persist to server
      archiveOffer(id);
      if (useServerOffers && locationId && token) {
        try {
          await toggleOfferStatusOnServer(id, locationId, 'archived', token);
          await fetchOffers(locationId, token, { force: true });
        } catch (e) {
          addToast(e instanceof Error ? e.message : 'Failed to archive offer on server', 'error');
        }
      }
      addToast('Offer archived — moved to Inactive', 'warning');
    } else if (type === 'pause') {
      await persistOfferStatus(id, 'paused', { msg: 'Offer paused', variant: 'warning' });
    }
    setConfirmAction(null);
    setSelectedIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
  };

  const handleDuplicate = (sourceId: string) => {
    const offer = offers.find((o) => o.id === sourceId);
    if (!offer) {
      addToast('Offer not found', 'error');
      return;
    }
    navigate(`/offers/duplicate/${encodeURIComponent(offer.id)}`);
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    const locationId = selectedBranch?.id ?? selectedBranch?.locationId ?? '';
    const token = auth?.accessToken ?? auth?.access_token ?? null;
    if (!locationId) {
      addToast('Location is required', 'error');
      return;
    }

    const ids = Array.from(selectedIds);
    let failed = 0;

    if (activeTab === 'active') {
      // ── Active tab: bulk-archive selected offers in a single request ──
      // Optimistically mark all as archived in local store first
      ids.forEach((id) => archiveOffer(id));

      if (useServerOffers && token) {
        try {
          await bulkArchiveOffersOnServer(ids, token);
        } catch {
          failed = ids.length;
        }
      }

      await fetchOffers(locationId, token, { force: true });
      addToast(
        `${ids.length - failed} offer(s) archived — moved to Inactive${failed > 0 ? ` (${failed} failed)` : ''}`,
        failed > 0 ? 'error' : 'warning',
      );
    } else {
      // ── Inactive / Draft tab: bulk-delete selected offers in a single request ──
      try {
        await bulkDeleteOffersOnServer(ids, locationId, token);
        ids.forEach((id) => deleteOffer(id));
      } catch {
        failed = ids.length;
      }

      await fetchOffers(locationId, token, { force: true });
      addToast(
        `${ids.length - failed} offer(s) deleted${failed > 0 ? ` (${failed} failed)` : ''}`,
        failed > 0 ? 'error' : 'warning',
      );
    }

    setSelectedIds(new Set());
  };


  const isOfferExpired = (o: Offer): boolean => {
    if (o.status === 'expired') return true;
    const today = new Date().toISOString().split('T')[0];
    return !o.schedule.noEndDate && Boolean(o.schedule.endDate && o.schedule.endDate < today);
  };

  // Catalog-style 32×32 ghost icon button with a Material Symbols glyph.
  const ActionIconButton = ({
    icon,
    title,
    danger = false,
    disabled = false,
    onClick,
  }: {
    icon: string;
    title: string;
    danger?: boolean;
    disabled?: boolean;
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  }) => (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      style={{
        ...offerListActionButtonStyle,
        ...(disabled ? { opacity: 0.3, cursor: 'not-allowed', pointerEvents: 'none' as const } : {}),
      }}
      onMouseEnter={!disabled ? (danger ? offerListActionEnterDanger : offerListActionEnterDefault) : undefined}
      onMouseLeave={!disabled ? (danger ? offerListActionLeaveDanger : offerListActionLeaveDefault) : undefined}
      onClick={(e) => { e.stopPropagation(); if (!disabled) onClick(e); }}
    >
      <span className="material-symbols-rounded" style={{ fontSize: OFFER_LIST_ACTION_ICON_PX }}>{icon}</span>
    </button>
  );

  const getStatusActions = (offer: Offer) => {
    const actions: React.ReactNode[] = [];
    if (offer.status === 'draft') {
      actions.push(
        <ActionIconButton
          key="activate"
          icon="play_arrow"
          title="Activate"
          onClick={() => void persistOfferStatus(offer.id, 'active', { msg: 'Offer activated', variant: 'success' })}
        />
      );
    }
    if (offer.status === 'active') {
      actions.push(
        <ActionIconButton
          key="pause"
          icon="pause"
          title="Pause"
          onClick={() => setConfirmAction({ type: 'pause', id: offer.id })}
        />
      );
    }
    if (offer.status === 'paused') {
      actions.push(
        <ActionIconButton
          key="resume"
          icon="play_arrow"
          title="Resume"
          onClick={() => void persistOfferStatus(offer.id, 'active', { msg: 'Offer reactivated', variant: 'success' })}
        />
      );
    }
    if (offer.status === 'archived') {
      actions.push(
        <ActionIconButton
          key="restore"
          icon="restore"
          title="Restore to Draft"
          onClick={() => void persistOfferStatus(offer.id, 'draft', { msg: 'Offer restored to Draft', variant: 'success' })}
        />
      );
    }
    return actions;
  };

  const SortHeader = ({ label, field }: { label: string; field: SortKey }) => (
    <span
      onClick={() => handleSort(field)}
      className="offers-sort-header"
    >
      {label}
      {sortKey === field && (
        <span className="offers-sort-arrow">{sortDir === 'asc' ? '▲' : '▼'}</span>
      )}
    </span>
  );

  const columns = [
    {
      key: 'select',
      label: '',
      width: 36,
      render: (_: any, row: Offer) => (
        <input
          type="checkbox"
          checked={selectedIds.has(row.id)}
          onChange={(e) => { e.stopPropagation(); handleToggleSelect(row.id); }}
          className="offers-checkbox"
        />
      ),
    },
    {
      key: 'name',
      label: <SortHeader label="Offer Name" field="name" />,
      render: (_: any, row: Offer) => (
        <div>
          <span className="offers-name">{row.name}</span>
          {row.promoCodes.length > 0 && (
            <span className="offers-code-meta">
              {row.promoCodes.filter((c) => c.status === 'active').length} active code{row.promoCodes.filter((c) => c.status === 'active').length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'type',
      label: <SortHeader label="Type" field="type" />,
      render: (_: any, row: Offer) => {
        const cfg = offerTypeLabels[row.type];
        return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
      },
    },
    {
      key: 'discount',
      label: <SortHeader label="Discount" field="discount" />,
      mono: true,
      render: (_: any, row: Offer) => (
        <span className="offers-discount">{formatDiscount(row)}</span>
      ),
    },
    {
      key: 'status',
      label: <SortHeader label="Status" field="status" />,
      render: (_: any, row: Offer) => (
        <div className="offers-status-cell">
          <StatusBadge status={row.status} />
          {row.origin === 'corporate' && <Badge variant="accent">Corp</Badge>}
          {row.isExclusive && <Badge variant="warning">Excl</Badge>}
        </div>
      ),
    },
    {
      key: 'orderTypes',
      label: 'Order Types',
      render: (_: any, row: Offer) => {
        const names = resolveOrderTypeNames(row.orderTypes);
        return (
          <span className="offers-small-text" title={names.join(', ')}>
            {names.length ? names.join(', ') : '—'}
          </span>
        );
      },
    },
    {
      key: 'createdAt',
      label: <SortHeader label="Created" field="createdAt" />,
      render: (_: any, row: Offer) => (
        <span className="offers-date-text">
          {formatDate(row.createdAt)}
        </span>
      ),
    },
    {
      key: 'redemptions',
      label: <SortHeader label="Uses" field="redemptions" />,
      mono: true,
      render: (_: any, row: Offer) => (
        <span className="offers-date-text">
          {row.redemptionCount.toLocaleString()}
          {row.eligibility.maxRedemptionsTotal ? ` / ${row.eligibility.maxRedemptionsTotal.toLocaleString()}` : ''}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      align: 'right' as const,
      render: (_: any, row: Offer) => (
        <div className="offers-actions">
          {!isOfferExpired(row) && (
            <ActionIconButton
              icon="edit"
              title="Edit"
              onClick={() => { setEditingOfferId(row.id); navigate(`/offers/${row.id}/edit`); }}
            />
          )}
          <ActionIconButton
            icon="content_copy"
            title="Duplicate"
            onClick={() => handleDuplicate(row.id)}
          />
          {getStatusActions(row)}
          {row.status === 'archived' ? (
            // Already archived: restore is shown by getStatusActions; archive is blurred/disabled
            <ActionIconButton
              icon="archive"
              title="Already Archived"
              disabled
              onClick={() => {}}
            />
          ) : row.status === 'draft' ? (
            // Draft offers get delete
            <ActionIconButton
              icon="delete"
              title="Delete"
              danger
              onClick={() => setConfirmAction({ type: 'delete', id: row.id })}
            />
          ) : (
            // Active, paused, expired — all get archive
            <ActionIconButton
              icon="archive"
              title="Archive"
              onClick={() => setConfirmAction({ type: 'archive', id: row.id })}
            />
          )}
        </div>
      ),
    },
  ];

  const staticConfirmMessages: Record<string, { title: string; message: string; confirmText: string }> = {
    delete: { title: 'Delete Offer', message: 'Permanently delete this offer? This cannot be undone.', confirmText: 'Delete' },
    archive: { title: 'Archive Offer', message: 'Archive this offer? It will be hidden from the default list but all historical data will be preserved.', confirmText: 'Archive' },
    pause: { title: 'Pause Offer', message: 'Pause this offer? It will be immediately removed from all order types and cannot be applied to new orders.', confirmText: 'Pause' },
  };
  const cm = useMemo(() => {
    if (!confirmAction) return null;
    if (confirmAction.type === 'archive') {
      const offer = offers.find((o) => o.id === confirmAction.id);
      if (offer?.status === 'expired') {
        return { title: 'Archive Expired Offer', message: 'Archive this expired offer? It can be restored to Draft later.', confirmText: 'Archive' };
      }
    }
    return staticConfirmMessages[confirmAction.type] ?? null;
  }, [confirmAction, offers]);

  return (
    <DesktopLayout>
    <div className="offers-page">
      <PageHeader
        icon={<IconTag size={24} color="rgba(255,255,255,0.95)" />}
        title="Offers & Discounts"
        subtitle="Create and manage promotions,happy hours, and more."
        actions={
          <Button variant="primary" size="md" onClick={() => navigate('/offers/new')}>
            + Create Offer
          </Button>
        }
      />

      {/* KPI Cards */}
       <KPIRow>
        <KPICard
          label="Active Offers"
          value={String(summary.totalActiveOffersCount)}
          icon={<IconTag size={18} />}
        />
        <KPICard
          label="Redemptions"
          value={formatNumber(summary.totalRedemptions)}
          icon={<IconActivity size={18} />}
        />
        <KPICard
          label="Discount Given"
          value={formatCurrency(summary.totalDiscountAmount)}
          icon={<IconDollar size={18} />}
        />
        <KPICard
          label="Avg Order Value"
          value={formatCurrency(summary.totalOrderAmount)}
        />
      </KPIRow>

      {/* Search + Type Filter + Page size */}
      <div className="offers-filters-row">
        <div className="offers-search-wrap">
          <SearchInput value={search} onChange={setSearch} placeholder="Search offers or promo codes..." />
        </div>
        <div className="offers-type-wrap">
          <Select
            label=""
            value={typeFilter}
            onChange={setTypeFilter}
            options={TYPE_FILTER_OPTIONS}
          />
        </div>
        <div className="offers-page-size-wrap">
          <span className="offers-page-size-label">Show</span>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="offers-page-size-select"
          >
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      <Tabs tabs={tabsWithCounts} activeKey={activeTab} onChange={setActiveTab} />

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="offers-bulk-bar">
          <span className="offers-bulk-count">
            {selectedIds.size} selected
          </span>
          <Button variant="ghost" size="sm" className="offers-btn-red" onClick={handleBulkDelete}>
            {activeTab === 'active' ? 'Archive Selected' : 'Delete Selected'}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
            Clear
          </Button>
        </div>
      )}

      {/* Loading / error states */}
      {showInitialLoading && (
        <div className="offers-loading">
          Loading offers…
        </div>
      )}
      {!showInitialLoading && offersError && (
        <div className="offers-error">
          {offersError}
        </div>
      )}

      {/* Table */}
      {!showInitialLoading && <DataTable
        columns={columns}
        data={paged}
        onRowClick={(row) => navigate(`/offers/${row.id}`)}
        emptyIcon={<IconTag size={36} strokeWidth={1.2} />}
        emptyMessage={
          search.trim() || typeFilter
            ? 'No offers match your filters'
            : 'No offers created yet'
        }
        emptyDescription={
          search.trim() || typeFilter
            ? 'Try a different search term or clear your filters.'
            : 'Create your first offer to start driving sales.'
        }
        emptyAction={
          !(search.trim() || typeFilter)
            ? <Button variant="primary" onClick={() => navigate('/offers/new')}>+ Create Offer</Button>
            : undefined
        }
      />}

      {/* Pagination */}
      {filtered.length > pageSize && (
        <div className="offers-pagination-wrap">
          <Pagination page={page} pageSize={pageSize} total={filtered.length} onPageChange={setPage} />
        </div>
      )}

      {/* Active filter summary */}
      {(search.trim() || typeFilter) && filtered.length > 0 && (
        <div className="offers-filter-summary">
          Showing {filtered.length} result{filtered.length !== 1 ? 's' : ''}
          <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setTypeFilter(''); }}>
            Clear filters
          </Button>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleConfirm}
        title={cm?.title || ''}
        message={cm?.message || ''}
        confirmText={cm?.confirmText || 'Confirm'}
        variant={confirmAction?.type === 'delete' ? 'danger' : 'warning'}
      />

    </div>
    </DesktopLayout>
  );
}
